/**
 * Types and functions to form a PlanarGraph from a collection of closed 2d paths
 * @module
 */
import RBush from "rbush";
import { aabb2Overlaps } from "./aabb2";
import { DIST_EPSILON, WindingDir } from "./core";
import type { CurveCurveIntersection } from "./intersectLine2Line2";
import { CurveCurvePointIntersection, CurveCurveRangeIntersection, intersectLine2Line2 } from "./intersectLine2Line2";
import { line2Len } from "./line2";
import type { PlanarGraph } from "./planarGraph";
import {
  planarGraphAdjacentFaceEdge,
  planarGraphAdjacentHalfEdge,
  planarGraphByVerticesEdges,
  planarGraphVertexStripToPolyLine2,
} from "./planarGraph";
import type { DCEL, Point, Segment } from "./polyclip/dcel";
import { dcelFromPolygons, dcelFromPolygonsSnapRound, DEFAULT_SNAP_ROUND_PRECISION } from "./polyclip/dcelFromPolygons";
import { polyLine2ToPolyClipPolygonLoop } from "./polyclip/polyClip";
import { polyLine2AABB2WithPadding, polyLine2WindingDir } from "./polyLine2";
import type { PolyLine2 } from "./polyLine2Types";
import type { IndexAABB2 } from "./rtree";
import { hashInts2, init2DArray } from "./utils";
import { vec2Lerp } from "./vec2";

const { abs } = Math;

export interface EdgeRef {
  readonly index: number;
  readonly edgeIndex: number;
}

export class PolygonPlanarGraph {
  constructor(
    readonly planarGraph: PlanarGraph,
    readonly polygonToPlanarGraph: EdgeRef[][][], // polygonIndex => polygonEdgeIndex => EdgeRef[]
    readonly planarGraphToPolygon: EdgeRef[][], // planarGraphFaceIndex => planarGraphEdgeIndex => EdgeRef
  ) {}

  halfEdgeIndexToPolygonEdgeRef(halfEdgeIndex: number): EdgeRef {
    const parentFace = this.planarGraph.halfEdgeParentFace[halfEdgeIndex];
    const faceHalfEdges = this.planarGraph.faceHalfEdges[parentFace];
    const halfEdgeIndexInFaceHalfEdges = faceHalfEdges.indexOf(halfEdgeIndex);
    return this.planarGraphToPolygon[parentFace][halfEdgeIndexInFaceHalfEdges];
  }

  polygonToPlanarGraphFace(polygonIndex: number): number {
    return this.polygonToPlanarGraph[polygonIndex][0][0].index;
  }

  planarGraphFaceToPolygon(planarGraphFaceIndex: number): number {
    return this.planarGraphToPolygon[planarGraphFaceIndex]?.[0]?.index;
  }

  halfEdgeIndexToPolygonEdgeIndex(halfEdgeIndex: number): number {
    return this.halfEdgeIndexToPolygonEdgeRef(halfEdgeIndex)?.edgeIndex;
  }

  halfEdgeIndexToPolygonIndex(halfEdgeIndex: number): number {
    return this.halfEdgeIndexToPolygonEdgeRef(halfEdgeIndex)?.index;
  }

  polygonNeighborsOnEdge(polygonId: number, polygonEdgeId: number): (EdgeRef | null)[] {
    const polygonEdgeIds = this.polygonToPlanarGraph[polygonId][polygonEdgeId];
    const faceId = this.polygonToPlanarGraph[polygonId][0][0].index;
    const faceHalfEdges = this.planarGraph.faceHalfEdges[faceId];

    const oppPolygons: (EdgeRef | null)[] = [];

    for (let i = 0, l = polygonEdgeIds.length; i < l; i++) {
      const { edgeIndex } = polygonEdgeIds[i];

      const opp = planarGraphAdjacentHalfEdge(faceHalfEdges[edgeIndex]);
      const parentFaceId = this.planarGraph.halfEdgeParentFace[opp];

      // if the opposite planar graph face is CW it is not from another block

      if (parentFaceId === undefined || this.planarGraph.faceWindingDirs[parentFaceId] === WindingDir.CW) {
        oppPolygons.push(null);
        continue;
      }

      // get the edge index of the opposing polygon

      const oppFaceHalfEdges = this.planarGraph.faceHalfEdges[parentFaceId];
      const oppFaceEdgeId = oppFaceHalfEdges.indexOf(opp);

      const polygonEdge = this.planarGraphToPolygon[parentFaceId][oppFaceEdgeId];

      if (polygonEdge) {
        oppPolygons.push(polygonEdge);
      }
    }

    return oppPolygons;
  }

  polygonNeighbors(polygonId: number) {
    const polygonEdgeIds = this.polygonToPlanarGraph[polygonId];
    const neighbors: (EdgeRef | null)[][] = [];

    for (let i = 0, l = polygonEdgeIds.length; i < l; i++) {
      neighbors.push(this.polygonNeighborsOnEdge(polygonId, i));
    }

    return neighbors;
  }
}

/**
 * Returns a new PolygonPlanarGraph with face indices reordered by `stableToOld`.
 * The face that was at index `stableToOld[newFaceIndex]` in `graph` is moved to `newFaceIndex`.
 */
export function permutePolygonPlanarGraphFaces(
  graph: PolygonPlanarGraph,
  stableToOld: readonly number[],
): PolygonPlanarGraph {
  const faceCount = graph.planarGraph.faceHalfEdges.length;
  if (stableToOld.length !== faceCount) {
    throw new Error(
      `permutePolygonPlanarGraphFaces expected ${faceCount} faces, got permutation of length ${stableToOld.length}`,
    );
  }

  const oldToStable = new Array<number>(faceCount).fill(-1);
  for (let newFaceIndex = 0; newFaceIndex < stableToOld.length; newFaceIndex++) {
    const oldFaceIndex = stableToOld[newFaceIndex];
    if (!Number.isInteger(oldFaceIndex) || oldFaceIndex < 0 || oldFaceIndex >= faceCount) {
      throw new Error(`Invalid face index ${oldFaceIndex} in permutation`);
    }
    if (oldToStable[oldFaceIndex] !== -1) {
      throw new Error(`Duplicate face index ${oldFaceIndex} in permutation`);
    }
    oldToStable[oldFaceIndex] = newFaceIndex;
  }

  const oldPlanarGraph = graph.planarGraph;
  const newPlanarGraph: PlanarGraph = {
    faceVertices: stableToOld.map((oldFaceIndex) => oldPlanarGraph.faceVertices[oldFaceIndex]),
    faceHalfEdges: stableToOld.map((oldFaceIndex) => oldPlanarGraph.faceHalfEdges[oldFaceIndex]),
    faceWindingDirs: stableToOld.map((oldFaceIndex) => oldPlanarGraph.faceWindingDirs[oldFaceIndex]),
    halfEdgeParentFace: oldPlanarGraph.halfEdgeParentFace.map((oldFaceIndex) => oldToStable[oldFaceIndex] ?? -1),
    vertexHalfEdges: oldPlanarGraph.vertexHalfEdges,
    halfEdgeVertexIndex: oldPlanarGraph.halfEdgeVertexIndex,
    halfEdges: oldPlanarGraph.halfEdges,
    vertexPositions: oldPlanarGraph.vertexPositions,
  };

  const newPolygonToPlanarGraph: EdgeRef[][][] = graph.polygonToPlanarGraph.map((polygonRefs) =>
    polygonRefs.map((edgeRefs) =>
      edgeRefs.map((edgeRef) => ({
        index: oldToStable[edgeRef.index],
        edgeIndex: edgeRef.edgeIndex,
      })),
    ),
  );

  const newPlanarGraphToPolygon: EdgeRef[][] = stableToOld.map(
    (oldFaceIndex) => graph.planarGraphToPolygon[oldFaceIndex],
  );

  return new PolygonPlanarGraph(newPlanarGraph, newPolygonToPlanarGraph, newPlanarGraphToPolygon);
}

export interface IllegalPolygonIntersection {
  readonly intersection: CurveCurveIntersection;
}

export function polygonPlanarGraphNew(polygons: PolyLine2[], useDCEL: boolean = false): PolygonPlanarGraph {
  const [graph] = polygonPlanarGraphNewDebug(polygons, false, useDCEL);
  return graph;
}

export class DCELPlanarGraphWrapper implements PlanarGraph {
  faceWindingDirs: WindingDir[];
  faceVertices: number[][];
  faceHalfEdges: number[][];
  halfEdgeParentFace: number[];
  vertexPositions: ArrayLike<number>;
  halfEdges: number[];
  vertexHalfEdges: number[][];
  halfEdgeVertexIndex: number[];

  readonly polygonToPlanarGraph: EdgeRef[][][]; // polygonIndex => polygonEdgeIndex => { index: number, edgeIndex: number }[]
  readonly planarGraphToPolygon: EdgeRef[][]; // planarGraphFaceIndex => planarGraphEdgeIndex => { index: number, edgeIndex: number }

  constructor(dcel: DCEL) {
    this.faceWindingDirs = dcel.faceSignedAreas.map((x) => (x < 0 ? WindingDir.CW : WindingDir.CCW));
    this.faceVertices = dcel.faceHalfEdges.map((x) => x.map((y) => dcel.halfEdges[y][0]));
    this.faceHalfEdges = dcel.faceHalfEdges;
    this.halfEdgeParentFace = dcel.halfEdgeToFace;
    this.vertexPositions = dcel.vertices.flatMap((x) => [x[0], x[1]]);
    this.halfEdges = dcel.halfEdges.flatMap((x) => [x[0], x[1]]);

    // DCEL.vertexHalfEdges are stored in a reverse order compared to PlanarGraph.vertexHalfEdges
    this.vertexHalfEdges = dcel.vertexHalfEdges.map((x) => x.slice().reverse());
    this.halfEdgeVertexIndex = dcel.halfEdges.map((x, i) => {
      const startVertex = x[0];
      return this.vertexHalfEdges[startVertex].length - 1 - dcel.halfEdgeVertexIndices[i];
    });

    this.polygonToPlanarGraph = [];

    for (let segmentIndex = 0; segmentIndex < dcel.segmentToPolygon.length; segmentIndex++) {
      const polygon = dcel.segmentToPolygon[segmentIndex];
      const edge = dcel.segmentToPolygonLoopEdge[segmentIndex];

      if (!this.polygonToPlanarGraph[polygon]) {
        this.polygonToPlanarGraph[polygon] = [];
      }

      if (!this.polygonToPlanarGraph[polygon][edge]) {
        this.polygonToPlanarGraph[polygon][edge] = [];
      }

      const halfEdges = dcel.segmentToHalfEdges[segmentIndex];

      for (let i = 0; i < halfEdges.length; i++) {
        const halfEdge = halfEdges[i];
        const faceIndex = dcel.halfEdgeToFace[halfEdge];

        this.polygonToPlanarGraph[polygon][edge].push({
          index: faceIndex,
          edgeIndex: dcel.faceHalfEdges[faceIndex].indexOf(halfEdge),
        });
      }
    }

    const halfEdgeToSegment: number[][] = [];

    for (let segmentIndex = 0; segmentIndex < dcel.segmentToHalfEdges.length; segmentIndex++) {
      const halfEdges = dcel.segmentToHalfEdges[segmentIndex];
      for (const halfEdge of halfEdges) {
        if (!halfEdgeToSegment[halfEdge]) {
          halfEdgeToSegment[halfEdge] = [];
        }

        halfEdgeToSegment[halfEdge].push(segmentIndex);
      }
    }

    this.planarGraphToPolygon = [];

    for (let faceIndex = 0; faceIndex < this.faceHalfEdges.length; faceIndex++) {
      this.planarGraphToPolygon[faceIndex] = [];

      const face = this.faceHalfEdges[faceIndex];

      for (let i = 0; i < face.length; i++) {
        const halfEdgeIndex = face[i];
        const polygon = dcel.halfEdgeToPolygon[halfEdgeIndex];

        if (polygon.length !== 1) {
          // overlapping condition
          continue;
        }

        const segment = halfEdgeToSegment[halfEdgeIndex][0];
        if (segment === undefined) {
          continue;
        }

        const edgeIndex = dcel.segmentToPolygonLoopEdge[segment];

        this.planarGraphToPolygon[faceIndex][i] = { index: polygon[0], edgeIndex };
      }
    }
  }
}

/**
 * Creates a planar graph from polygons and segments using new dcel.ts, dcelFromPolygons.ts approach.
 * The constraintSegments and constraintPts are inserted into the segments before the planar graph is created.

 * @note The IllegalPolygonIntersection array is currently not populated as we don't detect illegal intersections.
 *       Illegal intersections occur when two polygon edges cross each other at non-endpoint locations
 *       (i.e., true crossings in the middle of edges). If illegal intersection detection is required, consider using
 *       `polygonPlanarGraphNewDebug` with `useDCEL=false`, which uses the non-DCEL path that detects and reports such intersections
 *       when `debug=true` and `crossingCount === 2` (both edges have interior intersection points).
 *       To implement illegal intersection detection below: We can add a check to dcelFromPolygonsSnapRound to see if a half edge side corresponds to more than one polygon.
 * @note The illegal intersections are informational only; they don't cause failures or errors. They're collected for debugging/testing but not used in production code paths.
 */
export function polygonSegmentsPlanarGraphNew(
  polygons: PolyLine2[],
  segments: Segment[],
  segmentPolygonIds: number[],
  snapRoundPrecision: number = DEFAULT_SNAP_ROUND_PRECISION,
  constraintSegments: Segment[] = [],
  constraintPts: Point[] = [],
): [PolygonPlanarGraph, IllegalPolygonIntersection[]] {
  const dcel = dcelFromPolygons({
    polygons: polygons.map((x) => [polyLine2ToPolyClipPolygonLoop(x, false, true, WindingDir.CW)]),
    segments,
    snapRoundPrecision,
    segmentPolygonIds,
    constraintSegments,
    constraintPts,
  });
  const dcelPG = new DCELPlanarGraphWrapper(dcel);
  return [new PolygonPlanarGraph(dcelPG, dcelPG.polygonToPlanarGraph, dcelPG.planarGraphToPolygon), []];
}

export function polygonPlanarGraphNewDebug(
  polygons: PolyLine2[],
  debug: boolean = false,
  useDCEL: boolean = false,
  epsilon: number = DIST_EPSILON,
): [PolygonPlanarGraph, IllegalPolygonIntersection[]] {
  if (useDCEL) {
    /**
     * @note The IllegalPolygonIntersection array is currently not populated as we don't detect illegal intersections.
     *       Illegal intersections occur when two polygon edges cross each other at non-endpoint locations
     *       (i.e., true crossings in the middle of edges). If illegal intersection detection is required, consider using
     *       `polygonPlanarGraphNewDebug` with `useDCEL=false`, which uses the non-DCEL path that detects and reports such intersections
     *       when `debug=true` and `crossingCount === 2` (both edges have interior intersection points).
     *       To implement illegal intersection detection below: We can add a check to dcelFromPolygonsSnapRound to see if a half edge side corresponds to more than one polygon.
     * @note The illegal intersections are informational only; they don't cause failures or errors. They're collected for debugging/testing but not used in production code paths.
     */
    const dcel = dcelFromPolygonsSnapRound({
      polygons: polygons.map((x) => [polyLine2ToPolyClipPolygonLoop(x, false, true, WindingDir.CW)]),
      snapRoundPrecision: DEFAULT_SNAP_ROUND_PRECISION,
    });
    const dcelPG = new DCELPlanarGraphWrapper(dcel);

    return [new PolygonPlanarGraph(dcelPG, dcelPG.polygonToPlanarGraph, dcelPG.planarGraphToPolygon), []];
  }
  const polygonWindingDirs = new Array<WindingDir>(polygons.length);

  const polygonAABBs: IndexAABB2[] = polygons.map((x, index) => {
    polygonWindingDirs[index] = polyLine2WindingDir(x);
    return {
      ...polyLine2AABB2WithPadding(x, epsilon),
      index,
    };
  });

  // intersect all edges
  const { splitParamsAll, illegalIntersections } = getPolygonIntersectionSplits({
    polygonAABBs,
    polygons,
    useRTree: polygonAABBs.length > 100,
    debug,
  });

  // sort all of the edge split pts by parameter
  for (const i in splitParamsAll) {
    const splitParamsPolygon = splitParamsAll[i];
    for (const j in splitParamsPolygon) {
      const edgeSplits = splitParamsPolygon[j];
      edgeSplits.sort(numberSort);
      removeDuplicateParams(edgeSplits);
    }
  }

  // make all pts unique

  const ptRTree = new RBush<IndexAABB2>();
  const uniquePts: number[] = [];

  function tryAddPt(x: number, y: number): number {
    const sbox = pointRBoxNew(-1, x, y);

    const s = ptRTree.search(sbox);
    if (s.length > 0) {
      return s[0].index;
    }

    uniquePts.push(x);
    uniquePts.push(y);

    const index = uniquePts.length / 2 - 1;
    sbox.index = index;

    ptRTree.insert(sbox);

    return index;
  }

  // the unique vertex sequence making up a polygon boundary
  // polygonIndex => edgeIndex => uniqueVertexIndex
  const polygonUniquePtsAll: TripleArray<number> = new Array(polygons.length);

  for (let i = 0, l = polygons.length; i < l; i++) {
    const polygon = polygons[i];

    const polygonUniquePts: number[][] = [];
    polygonUniquePtsAll[i] = polygonUniquePts;

    for (let j = 0, lj = polygon.length - 2; j < lj; j += 2) {
      const ax = polygon[j];
      const ay = polygon[j + 1];

      const bx = polygon[j + 2];
      const by = polygon[j + 3];

      const splitParams = doubleNumMapGet(splitParamsAll, i, j);

      const edgeUniquePts: number[] = [];
      polygonUniquePts.push(edgeUniquePts);

      // start pt in original polygon edge
      edgeUniquePts.push(tryAddPt(ax, ay));

      if (splitParams) {
        const len = line2Len(ax, ay, bx, by);
        for (const splitParam of splitParams) {
          const [ix, iy] = vec2Lerp(ax, ay, bx, by, splitParam / len);
          edgeUniquePts.push(tryAddPt(ix, iy));
        }
      }

      // end pt in original polygon edge
      edgeUniquePts.push(tryAddPt(bx, by));
    }
  }

  // make all edges unique

  const uniqueEdgeMapping = {};
  const uniqueEdges: number[] = [];

  interface PolygonSplitEdge {
    polygonIndex: number;
    edgeIndex: number;
    subEdgeIndex: number;
  }

  const edgeMapFwd: { [uniqueEdgeIndex: number]: PolygonSplitEdge[] } = {};

  // for each polygon
  for (let i = 0, l = polygonUniquePtsAll.length; i < l; i++) {
    const polygonUniquePts = polygonUniquePtsAll[i];

    // for each edge in the original polygon
    for (let j = 0, lj = polygonUniquePts.length; j < lj; j++) {
      const edgeUniquePts = polygonUniquePts[j];

      // for each split edge of the split polygon edge
      for (let k = 0, lk = edgeUniquePts.length - 1; k < lk; k++) {
        // lookup the the unique pts that make up this edge
        const e0 = edgeUniquePts[k];
        const e1 = edgeUniquePts[k + 1];

        const hash = hashInts2(e0, e1);
        let uniqueEdgeIndex = uniqueEdgeMapping[hash];

        const mapping = { polygonIndex: i, edgeIndex: j, subEdgeIndex: k };

        // if we already encountered this edge, we don't insert it into the unique edge list
        if (uniqueEdgeIndex !== undefined) {
          // store mapping
          if (!edgeMapFwd[uniqueEdgeIndex]) {
            edgeMapFwd[uniqueEdgeIndex] = [];
          }
          edgeMapFwd[uniqueEdgeIndex].push(mapping);

          continue;
        }

        uniqueEdgeIndex = uniqueEdges.length / 2;
        uniqueEdgeMapping[hash] = uniqueEdgeIndex;
        uniqueEdgeMapping[hashInts2(e1, e0)] = uniqueEdgeIndex;

        uniqueEdges.push(e0, e1);

        // store mapping
        if (!edgeMapFwd[uniqueEdgeIndex]) {
          edgeMapFwd[uniqueEdgeIndex] = [];
        }
        edgeMapFwd[uniqueEdgeIndex].push(mapping);
      }
    }
  }

  // now that we have made all of the vertices unique and all of the edges are non-overlapping
  // and referring to unique vertices, we generate the planar graph

  const planarGraph = planarGraphByVerticesEdges(uniquePts, uniqueEdges);

  // build mapping structure from polygon edge to planar graph and reverse
  const { faceHalfEdges, halfEdges } = planarGraph;
  const planarGraphToPolygon: EdgeRef[][] = init2DArray(faceHalfEdges.length);
  const polygonToPlanarGraph: EdgeRef[][][] = [];

  // for each planar graph face
  for (let i = 0, l = faceHalfEdges.length; i < l; i++) {
    const curFaceHalfEdges = faceHalfEdges[i];
    const planarGraphFaceIndex = i;

    // for each start vertex in the face
    for (let j = 0, lj = curFaceHalfEdges.length; j < lj; j++) {
      const planarGraphFaceEdgeIndex = j;

      // get the src edge of the half edge
      const srcEdge = (curFaceHalfEdges[j] / 2) | 0;

      // the src edge is produced from potentially multiple polygon edges, we need mappings for each one
      const edgeMapping = edgeMapFwd[srcEdge];

      for (let k = 0, lk = edgeMapping.length; k < lk; k++) {
        const { polygonIndex, edgeIndex, subEdgeIndex } = edgeMapping[k];

        const polyEdgeStartVertex = doubleNumMapGet(polygonUniquePtsAll, polygonIndex, edgeIndex)?.[subEdgeIndex];
        const planarGraphEdgeStartVertex =
          halfEdges[2 * planarGraph.faceHalfEdges[planarGraphFaceIndex][planarGraphFaceEdgeIndex]];

        if (polyEdgeStartVertex === planarGraphEdgeStartVertex) {
          let correctedPlanarGraphFaceIndex = planarGraphFaceIndex;
          let correctedPlanarGraphFaceEdgeIndex = planarGraphFaceEdgeIndex;

          // if the polygon winding direction is CW (opposite to the winding dir of an interior planar graph face)
          // we need to look up the neighboring half edge which is the true parent of this edge
          if (polygonWindingDirs[polygonIndex] === WindingDir.CW) {
            const backEdge = planarGraphAdjacentFaceEdge(planarGraph, planarGraphFaceIndex, planarGraphFaceEdgeIndex);
            correctedPlanarGraphFaceIndex = backEdge.faceIndex;
            correctedPlanarGraphFaceEdgeIndex = backEdge.edgeIndex;
          }

          tripleArraySet(polygonToPlanarGraph, polygonIndex, edgeIndex, subEdgeIndex, {
            index: correctedPlanarGraphFaceIndex,
            edgeIndex: correctedPlanarGraphFaceEdgeIndex,
          });

          planarGraphToPolygon[correctedPlanarGraphFaceIndex][correctedPlanarGraphFaceEdgeIndex] = {
            index: polygonIndex,
            edgeIndex,
          };
        }
      }
    }
  }

  return [new PolygonPlanarGraph(planarGraph, polygonToPlanarGraph, planarGraphToPolygon), illegalIntersections];
}

export function polygonPlanarGraphFaces(graph: PolygonPlanarGraph, ignoreLimbs: boolean = false): PolyLine2[][] {
  const { faceHalfEdges, halfEdges, halfEdgeParentFace, faceVertices } = graph.planarGraph;

  const facePolylines: PolyLine2[][] = [];

  for (let faceIndex = 0; faceIndex < faceHalfEdges.length; faceIndex++) {
    let faceVerts: number[];
    if (ignoreLimbs) {
      faceVerts = [];

      for (let i = 0; i < faceHalfEdges[faceIndex].length; i++) {
        const halfEdgeIndex = faceHalfEdges[faceIndex][i];
        if (halfEdgeParentFace[halfEdgeIndex] === halfEdgeParentFace[planarGraphAdjacentHalfEdge(halfEdgeIndex)]) {
          // skip half edge that is part of the same face
          continue;
        }

        faceVerts.push(halfEdges[halfEdgeIndex * 2]);
      }

      if (faceVerts.length === 0) {
        facePolylines.push([]);
        continue;
      }
    } else {
      faceVerts = faceVertices[faceIndex];
    }

    const faceLoop = planarGraphVertexStripToPolyLine2(graph.planarGraph, faceVerts);
    facePolylines.push([faceLoop]);
  }
  return facePolylines;
}

type TripleArray<T> = T[][][];

export function getPolygonIntersectionSplits({
  polygonAABBs,
  polygons,
  useRTree,
  debug,
}: {
  polygonAABBs: IndexAABB2[];
  polygons: PolyLine2[];
  useRTree: boolean;
  debug: boolean;
}): { splitParamsAll: DoubleNumMap<number>; illegalIntersections: IllegalPolygonIntersection[] } {
  const splitParamsAll: DoubleNumMap<number> = {}; // polygonIndex => edgeIndex => split parameters
  const tried: { [hash: number]: boolean } = {};
  const illegalIntersections: IllegalPolygonIntersection[] = [];

  if (useRTree) {
    const polygonRTree = new RBush<IndexAABB2>();
    polygonRTree.load(polygonAABBs);

    for (let i = 0, l = polygons.length; i < l; i++) {
      processPolygonIntersectionCandidates({
        polygon: polygons[i],
        polyInts: polygonRTree.search(polygonAABBs[i]),
        i,
        tried,
        polygons,
        splitParamsAll,
        illegalIntersections,
        debug,
      });
    }

    return { splitParamsAll, illegalIntersections };
  }

  for (let i = 0, l = polygons.length; i < l; i++) {
    const polyInts: IndexAABB2[] = [];
    for (let j = i + 1; j < l; j++) {
      const test = polygonAABBs[j];
      if (aabb2Overlaps(polygonAABBs[i], test, 0)) {
        polyInts.push(test);
      }
    }

    processPolygonIntersectionCandidates({
      polygon: polygons[i],
      polyInts,
      i,
      tried,
      polygons,
      splitParamsAll,
      illegalIntersections,
      debug,
    });
  }

  return { splitParamsAll, illegalIntersections };
}

function processPolygonIntersectionCandidates({
  polyInts,
  i,
  tried,
  polygons,
  polygon,
  splitParamsAll,
  debug,
  illegalIntersections,
}: {
  polyInts: IndexAABB2[];
  i: number;
  tried: { [hash: number]: boolean };
  polygons: PolyLine2[];
  polygon: PolyLine2;
  splitParamsAll: DoubleNumMap<number>;
  debug: boolean;
  illegalIntersections: IllegalPolygonIntersection[];
}) {
  for (const polyInt of polyInts) {
    const polyIntIndex = polyInt.index;

    if (polyIntIndex === i || tried[hashInts2(i, polyIntIndex)]) {
      continue;
    }

    tried[hashInts2(i, polyIntIndex)] = true;
    tried[hashInts2(polyIntIndex, i)] = true;

    const opposingPolygon = polygons[polyIntIndex];

    for (let j = 0, lj = polygon.length - 2; j < lj; j += 2) {
      const x1 = polygon[j];
      const y1 = polygon[j + 1];

      const x2 = polygon[j + 2];
      const y2 = polygon[j + 3];

      const l0 = line2Len(x1, y1, x2, y2);

      for (let k = 0, lk = opposingPolygon.length - 2; k < lk; k += 2) {
        const x3 = opposingPolygon[k];
        const y3 = opposingPolygon[k + 1];

        const x4 = opposingPolygon[k + 2];
        const y4 = opposingPolygon[k + 3];

        const edgeInt = intersectLine2Line2(x1, y1, x2, y2, x3, y3, x4, y4, 0.001);
        if (!edgeInt) {
          continue;
        }

        const l1 = line2Len(x3, y3, x4, y4);

        if (edgeInt instanceof CurveCurvePointIntersection) {
          const { param0, param1 } = edgeInt;

          // end pt, crossing
          // crossing, end pt
          // end pt, end pt
          // what cases are allowed, not allowed?
          let crossingCount = 0;

          if (inParamRangeNoExtrema(0, l0, param0)) {
            crossingCount++;
            doubleNumMapPush(splitParamsAll, i, j, param0);
          }

          if (inParamRangeNoExtrema(0, l1, param1)) {
            crossingCount++;
            doubleNumMapPush(splitParamsAll, polyIntIndex, k, param1);
          }

          if (debug && crossingCount === 2) {
            illegalIntersections.push({ intersection: edgeInt });
          }
        } else if (edgeInt instanceof CurveCurveRangeIntersection) {
          const { startParam0, startParam1, endParam0, endParam1 } = edgeInt;

          if (inParamRangeNoExtrema(0, l0, startParam0)) {
            doubleNumMapPush(splitParamsAll, i, j, startParam0);
          }

          if (inParamRangeNoExtrema(0, l0, endParam0)) {
            doubleNumMapPush(splitParamsAll, i, j, endParam0);
          }

          if (inParamRangeNoExtrema(0, l1, startParam1)) {
            doubleNumMapPush(splitParamsAll, polyIntIndex, k, startParam1);
          }

          if (inParamRangeNoExtrema(0, l1, endParam1)) {
            doubleNumMapPush(splitParamsAll, polyIntIndex, k, endParam1);
          }
        }
      }
    }
  }
}

function tripleArrayGetAlloc<T>(map: TripleArray<T>, a: number, b: number): T[] {
  const aset = map[a] || (map[a] = []);
  return aset[b] || (aset[b] = []);
}

function tripleArraySet<T>(map: TripleArray<T>, a: number, b: number, c: number, value: T) {
  const arr = tripleArrayGetAlloc(map, a, b);
  arr[c] = value;
}

export interface DoubleNumMap<T> {
  [a: number]: { [b: number]: T[] };
}

function doubleNumMapGet<T>(map: DoubleNumMap<T>, a: number, b: number): T[] | undefined {
  const aset = map[a];
  return aset ? aset[b] : undefined;
}

function doubleNumMapGetAlloc<T>(map: DoubleNumMap<T>, a: number, b: number): T[] {
  const aset = map[a] || (map[a] = {});
  return aset[b] || (aset[b] = []);
}

function doubleNumMapPush<T>(map: DoubleNumMap<T>, a: number, b: number, value: T) {
  const arr = doubleNumMapGetAlloc(map, a, b);
  arr.push(value);
}

function inParamRangeNoExtrema(start: number, end: number, t: number) {
  return t > start + 0.001 && t < end - 0.001;
}

function pointRBoxNew(i: number, x: number, y: number): IndexAABB2 {
  return {
    minX: x - 0.001,
    minY: y - 0.001,
    maxX: x + 0.001,
    maxY: y + 0.001,
    index: i,
  };
}

function removeDuplicateParams(sorted: number[]) {
  let cursor = 1;
  for (let i = 0, l = sorted.length - 1; i < l; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];

    sorted[cursor] = b;

    if (abs(a - b) > 0.001) {
      cursor++;
    }
  }

  sorted.length = cursor;
  return sorted;
}

function numberSort(a: number, b: number) {
  return a - b;
}
